import type { IFocal, Kit, PartNode, ProductAPIResponse } from '@partful/shared-types';

export const makeProduct = (bom: PartNode = exampleBom): ProductAPIResponse => ({
  productId: 'test-product-id',
  name: 'test-name',
  customerRef: 'test-customer-ref',
  modelUrl: '/test-model-url',
  createdAt: '2020-11-25T11:42:22.082Z',
  createdById: 'test-created-by-id',
  updatedAt: '2020-11-25T11:42:22.082Z',
  updatedBy: 'test-updated-by-id',
  modelStatus: 'COMPLETED',
  accountId: 'test-account-id',
  thumbnailFilename: 'test-thumbnail-filename',
  bom,
});

export const makePartInstances = (parts: PartNode[], instanceId: number): PartNode[] =>
  parts.map((part) => ({
    ...part,
    glbIds: [`${part.id}-instance-${instanceId}`],
  }));

export const exampleFocal: IFocal = {
  target: [0, 0, 0],
  position: [1, 1, 1],
};

export const kitName = 'Test Kit Name';
export const externalId = 'TEST-KIT-ID-123';
export const kitNotes = 'This is a set of example notes for a given kit. It can be expected to span multiple lines';

export const makeKit = (partialKit?: Partial<Kit>): Kit => ({
  configurationPID: 'pid',
  version: '1.0',
  externalId,
  name: kitName,
  description: 'kit description',
  prices: [{ amount: 100, currency: 'GBP' }],
  components: [
    {
      externalId: 'componentId',
      name: 'componentName',
      quantity: 2,
    },
  ],
  url: '/kits/pid',
  ...partialKit,
});

export const exampleBom: PartNode = {
  id: 'A-0000',
  partNumber: 'A-0000',
  price: 379.99,
  name: 'test-root-name',
  kits: [makeKit()],
  children: [
    {
      id: 'A-0020',
      partNumber: 'A-0020',
      name: 'test-group-name',
      focal: exampleFocal,
      children: [
        {
          id: 'A-0010',
          partNumber: 'A-0010',
          name: 'test-part-name',
          children: [
            {
              id: 'A-0040',
              partNumber: 'A-0040',
              name: 'test-duplicate-part',
              children: [],
              glbIds: ['A-0040-instance-01'],
              object3DName: 'A-0040-instance-01',
            },
            {
              id: 'A-0040',
              partNumber: 'A-0040',
              name: 'test-duplicate-part-sibling',
              children: [],
              glbIds: ['A-0040-instance-02'],
              object3DName: 'A-0040-instance-02',
            },
          ],
        },
        {
          id: 'A-0030',
          partNumber: 'A-0030',
          name: 'test-nested-focal-name',
          focal: exampleFocal,
          defaultVariant: true,
          children: [
            {
              id: 'A-0040',
              partNumber: 'A-0040',
              name: 'test-duplicate-part-cousin',
              children: [],
              glbIds: ['A-0040-instance-03'],
              object3DName: 'A-0040-instance-03',
            },
            {
              id: 'A-0040',
              partNumber: 'A-0040',
              name: 'test-duplicate-part-cousin2',
              children: [],
              glbIds: ['A-0040-instance-04'],
              object3DName: 'A-0040-instance-04',
            },
            {
              id: 'SpecialId$@?&;:=+',
              partNumber: 'SpecialId$@?&;:=+',
              name: 'Special name',
              children: [],
              glbIds: ['SpecialObject3DName$@?&;:=+'],
              object3DName: 'SpecialObject3DName$@?&;:=+',
            },
          ],
        },
        {
          id: 'Neptune',
          partNumber: 'Neptune',
          name: 'Neptune',
          focal: {
            position: [-3.6468540568475256, 0.6265954934346546, 2.5046973339995726],
            target: [0, 0.4761757947018609, 2.30487816034294],
            hide: [
              'Alpha',
              'Beta',
              'Gamma',
              'Delta',
              'Wheel001',
              'Bolt004',
              'Bolt005',
              'Epsilon',
              'Eta',
              'Zeta',
              'Bolt',
              'Bolt001',
              'Bolt002',
              'Bolt003',
              'Wheel',
            ],
          },
          children: [
            {
              id: 'eded',
              partNumber: 'eded',
              name: 'edokedoked',
              objectType: 'product-option',
              children: [
                {
                  id: 'Iota',
                  partNumber: 'IotaDifferentThanId',
                  name: 'Iota',
                  attributes: {
                    'Part Number': 'S-009',
                    Shape: 'Cube',
                    Colour: 'Green',
                    Description: 'A single part, that is unique in the model. ',
                  },
                  children: [],
                  glbIds: ['Iota'],
                  object3DName: 'Iota',
                },
                {
                  id: 'Iota_Variant',
                  partNumber: 'Iota_Variant',
                  name: 'Iota Variant',
                  attributes: {
                    'Part Number': 'S-009',
                    Shape: 'Cube',
                    Colour: 'Green',
                    Description: 'A single part, that is unique in the model. ',
                  },
                  children: [],
                  glbIds: ['Iota_Variant'],
                  object3DName: 'Iota_Variant',
                },
              ],
            },
            {
              id: 'eded',
              partNumber: 'eded',
              name: 'edokedoked',
              objectType: 'product-option',
              children: [
                {
                  id: 'Kappa',
                  partNumber: 'Kappa',
                  name: 'Kappa',
                  attributes: {
                    'Part Number': 'S-010',
                    Shape: 'Cube',
                    Colour: 'Green',
                    Description: 'A single part, that is unique in the model. ',
                  },
                  children: [],
                  glbIds: ['Kappa'],
                  object3DName: 'Kappa',
                },
                {
                  id: 'Kappa_Variant',
                  partNumber: 'Kappa_Variant',
                  name: 'Kappa Variant',
                  attributes: {
                    'Part Number': 'S-010',
                    Shape: 'Cube',
                    Colour: 'Green',
                    Description: 'A single part, that is unique in the model. ',
                  },
                  children: [],
                  glbIds: ['Kappa_Variant'],
                  object3DName: 'Kappa_Variant',
                },
              ],
            },
            {
              id: 'eded',
              partNumber: 'eded',
              name: 'edokedoked',
              objectType: 'product-option',
              children: [
                {
                  id: 'Theta',
                  partNumber: 'Theta',
                  name: 'Theta',
                  attributes: {
                    'Part Number': 'S-008',
                    Shape: 'Cube',
                    Colour: 'Green',
                    Description: 'A single part, that is unique in the model. ',
                  },
                  children: [],
                  glbIds: ['Theta'],
                },
                {
                  id: 'Theta_Variant',
                  partNumber: 'Theta_Variant',
                  name: 'Theta Variant',
                  attributes: {
                    'Part Number': 'S-008',
                    Shape: 'Cube',
                    Colour: 'Green',
                    Description: 'A single part, that is unique in the model. ',
                  },
                  children: [],
                  glbIds: ['Theta_Variant'],
                  object3DName: 'Theta_Variant',
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

export const exampleProduct: ProductAPIResponse = makeProduct();

export const exampleMatchReport = {
  salesBOMRunID: '01J15495VGN6N0FFHHVK9P8QMY',
  configurationPID: '01J15495VGN6N0FFHHVK9P8QMY',
  totalPartsUploaded: 10,
  successfulylMatched: 7,
  unsuccessfullyMatched: 3,
};

export const kitData = {
  configurationPID: 'pid',
  version: '1.0',
  externalId,
  name: kitName,
  description: kitNotes,
  prices: [{ amount: 100, currency: 'GBP' }],
  components: [
    {
      externalId: 'CEI-123',
      name: 'Fillet1',
      quantity: 2,
    },
    {
      externalId: 'CEI-456',
      name: 'Revolve1',
      quantity: 1,
    },
    {
      externalId: 'CEI-789',
      name: 'Revolve-Thin2',
      quantity: 1,
    },
  ],
  url: '/kits/pid',
};

export const exampleKitNavigation = {
  kitNamedLink: [
    {
      kitData,
      name: 'kitName',
      url: 'kit-url',
    },
  ],
  parent: [
    {
      id: 'exampleProductId',
      name: 'exampleProductName',
      url: 'example-product-url',
    },
  ],
};
